import BridgeHelper from "@/utils/rn-bridge/bridge-helper";
import { NavCode } from "@/utils/rn-bridge/nav-code";
import { UserManager } from "@/service/user-manager";
import Log from "@repo/utils/log";

/** bridge 取登录态的等待上限；超时即回落到 URL，避免卡住首屏 */
const BRIDGE_TIMEOUT_MS = 2000;

/** RN 回包不带 expire，与 URL 注入保持一致：token 默认 6 小时有效（毫秒） */
const DEFAULT_TTL_MS = 6 * 60 * 60 * 1000;

export type LoginSessionSource = "bridge" | "url";

export interface ResolvedLoginSession {
  token: string;
  expire: number;
  source: LoginSessionSource;
}

function isInRNWebView(): boolean {
  return typeof window !== "undefined" && !!window.ReactNativeWebView?.postMessage;
}

/**
 * RN 对 GET_AUTH_TOKEN 的回包是 `JSON.stringify({ token })`，未登录时为 null。
 * 兼容已被 bridge 解析成对象、或直接给纯 token 字符串的情况；拿不到统一返回空串。
 */
function extractToken(payload: unknown): string {
  if (payload === null || payload === undefined) return "";
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed || trimmed === "null") return "";
    if (trimmed.startsWith("{")) {
      try {
        return extractToken(JSON.parse(trimmed));
      } catch {
        return "";
      }
    }
    return trimmed;
  }
  if (typeof payload === "object") {
    const token = (payload as { token?: unknown }).token;
    return typeof token === "string" ? token : "";
  }
  return "";
}

let bridgeAttempt: Promise<string> | null = null;

/**
 * 通过 rn-bridge 向 RN 索取 token。整个页面生命周期只问一次；未登录 / 失败 / 超时都返回空串。
 */
function requestTokenFromBridge(): Promise<string> {
  if (bridgeAttempt) return bridgeAttempt;

  // 浏览器里调试时没有 RN 宿主，直接跳过
  if (!isInRNWebView()) {
    bridgeAttempt = Promise.resolve("");
    return bridgeAttempt;
  }

  bridgeAttempt = BridgeHelper.sendMessageResult(
    { nav_code: NavCode.GET_AUTH_TOKEN },
    BRIDGE_TIMEOUT_MS,
  )
    .then(extractToken)
    .catch((err) => {
      Log.debug("从 RN 获取登录态失败，回落到 URL:", err);
      return "";
    });
  return bridgeAttempt;
}

/**
 * 解析本次进入页面应使用的登录态：优先 rn-bridge，其次 URL `?t=`。
 * 两者都没有时返回 undefined，由调用方沿用 sessionStorage 里已有的会话。
 */
export async function resolveLoginSession(
  urlToken?: string,
): Promise<ResolvedLoginSession | undefined> {
  const bridgeToken = await requestTokenFromBridge();
  const expire = Date.now() + DEFAULT_TTL_MS;

  if (bridgeToken) {
    return { token: bridgeToken, expire, source: "bridge" };
  }
  if (urlToken) {
    return { token: urlToken, expire, source: "url" };
  }
  return undefined;
}

/**
 * 把解析到的登录态写入 sessionStorage 并广播 tokenChanged；token 未变化时不重复广播。
 */
export function applyLoginSession(session: ResolvedLoginSession): void {
  const current = UserManager.getToken();
  UserManager.saveSessionData(session.token, session.expire);
  if (current === session.token) return;

  window.dispatchEvent(
    new CustomEvent("tokenChanged", {
      detail: {
        action: "login",
        token: session.token,
        timestamp: Date.now(),
        source: session.source,
      },
    }),
  );
}
